<script lang="ts" setup>
import CMoveModal from '#layers/core/app/components/filemanager/modals/MoveModal.vue';
import CFoldersTree from '#layers/core/app/components/filemanager/FoldersTree.vue';
import type { MovedResources, Scope } from '#layers/core/app/runtime/services/StorageService';

const { $t } = useLocalization();

const props = withDefaults(defineProps<{
    scope: any;
    destination?: number | null;
    resources?: any[]; // expects FileResource[]
    canMove?: boolean;
    openSelector?: boolean;
}>(), {
    destination: null,
    resources: () => [],
    canMove: true,
    openSelector: false
});

const emit = defineEmits<{
    (e: 'moved', result: MovedResources): void;
    (e: 'pathChanged', scope: Scope, destination: number | null): void;
}>();

const modalRef = ref<any>(null);
const formScope = ref<Scope>('public');
const formDestination = ref<number>(null);
const expanded = ref([0]);

const uniqueScopes = computed(() => [...new Set((props.resources || []).map((i: any) => i.scope))]);
const showButton = computed(() => props.canMove && (props.resources?.length || 0) > 0 && uniqueScopes.value.length === 1);

const initializeFolderSelection = (): void =>
{
    formScope.value = props.scope || 'public';
    formDestination.value = props.destination;
    modalRef.value?.show();
};

watch(() => props.openSelector, (v) =>
{
    if (v) initializeFolderSelection();
});

const selectPath = (): void =>
{
    emit('pathChanged', formScope.value, formDestination.value);
    modalRef.value?.hide();
};

const cancelPathSelection = (): void =>
{
    modalRef.value?.hide();
};

const onMoved = (r: MovedResources): void =>
{
    emit('moved', r);
};
</script>

<template>
    <div v-if="showButton" class="mt-2 d-flex gap-2">
        <ActionButton
            variant="secondary"
            icon="far fa-folder-open"
            class="w-100"
            :text="$t('[[[Zmień ścieżkę zapisu]]]')"
            @click.prevent="initializeFolderSelection"
        />
        <CMoveModal
            ref="moveModalRef"
            :scope="formScope"
            :destination="formDestination"
            :expanded="expanded"
            @moved="onMoved"
        />
        <IdeoModal
            ref="modalRef"
            :title="$t('[[[Wybierz ścieżkę zapisu]]]')"
            size="md"
            centered
            hide-footer
            modal-class="filemanager-modal"
            backdrop-class="filemanager-backdrop"
        >
            <template #default>
                <p>{{ $t('[[[Wybierz katalog do którego chcesz przenieść wybrane elementy.]]]') }}</p>
                <div>
                    <CFoldersTree
                        ref="foldersTreeRef"
                        v-model:scope="formScope"
                        v-model:destination="formDestination"
                        v-model:expanded="expanded"
                        root-folder
                        class="mb-3"
                    />

                    <div class="text-end">
                        <ActionButton
                            variant="primary"
                            icon="fas fa-check"
                            :text="$t('[[[Ustaw ścieżkę]]]')"
                            @click.prevent="selectPath"
                        />
                        <ActionButton
                            variant="secondary"
                            icon="far fa-times"
                            :text="$t('[[[Anuluj]]]')"
                            @click.prevent="cancelPathSelection"
                        />
                    </div>
                </div>
            </template>
        </IdeoModal>
    </div>
</template>
